import type { ParsedUrlQuery } from 'querystring'

export type ExtractRouteParams<S extends string> = S extends `${infer Before}[[...${infer P}]]${infer After}`
  ? ExtractRouteParams<Before> & { [K in P]?: string[] } & ExtractRouteParams<After>
  : S extends `${infer Before}[...${infer P}]${infer After}`
    ? ExtractRouteParams<Before> & { [K in P]: string[] } & ExtractRouteParams<After>
    : S extends `${infer Before}[${infer P}]${infer After}`
      ? ExtractRouteParams<Before> & { [K in P]: string } & ExtractRouteParams<After>
      : {}

/**
 * Resolves a Next.js route pattern with the given params.
 *
 * @example
 * getRoute('/login/[[...mode]]', { mode: ['employee'] }) // '/login/employee'
 * getRoute('/login/[[...mode]]', {})                     // '/login'
 * getRoute('/diagnosis/test-personalised/[slug]', { slug: '5' }) // '/diagnosis/test-personalised/5'
 * getRoute(router.pathname, router.query)                // works with router too
 */
export const getRoute = <S extends string>(
  pattern: S,
  params: ExtractRouteParams<S> = {} as ExtractRouteParams<S>,
): string => {
  let route: string = pattern

  // optional catch-all: /[[...param]] → /a/b or ''
  route = route.replace(/\/\[\[\.\.\.(\w+)\]\]/g, (_, key) => {
    const value = (params as ParsedUrlQuery)[key]
    if (!value || (Array.isArray(value) && value.length === 0)) return ''
    return '/' + (Array.isArray(value) ? value.join('/') : value)
  })

  // required catch-all: /[...param] → /a/b
  route = route.replace(/\/\[\.\.\.(\w+)\]/g, (_, key) => {
    const value = (params as ParsedUrlQuery)[key]
    if (!value) throw new Error(`getRoute: missing required catch-all param "${key}"`)
    return '/' + (Array.isArray(value) ? value.join('/') : value)
  })

  // dynamic segment: [param] → value
  route = route.replace(/\[(\w+)\]/g, (_, key) => {
    const value = (params as ParsedUrlQuery)[key]
    if (value === undefined || value === null) throw new Error(`getRoute: missing required param "${key}"`)
    if (Array.isArray(value)) throw new Error(`getRoute: param "${key}" must be a string, got an array`)
    return value
  })

  return route
}
